
<template>
  <Teleport to="body">
    <Transition name="role-modal">
      <div
        v-if="visible && companyEmployee"
        class="role-overlay"
        :class="{ 'dark-theme': isDarkTheme }"
        @click.self="closeModal"
      >
        <div
          ref="modalRef"
          class="role-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="role-title"
          aria-describedby="role-description"
          tabindex="-1"
        >
          <!-- =====================================================
               TOP — COMPANY IDENTITY
          ====================================================== -->

          <div class="modal-top">
            <div class="company-identity">
              <div class="brand-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect
                    x="4"
                    y="3"
                    width="16"
                    height="18"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />
                  <path
                    d="M9 8H10M14 8H15M9 12H10M14 12H15"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                  />
                  <path
                    d="M10 21V17H14V21"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />
                </svg>
              </div>

              <div class="company-identity-content">
                <span class="company-identity-label">
                  ВАША КОМПАНИЯ
                </span>

                <div class="company-name-highlight">
                  <span class="company-name-dot"></span>

                  <span class="company-name-text">
                    {{ companyEmployee.companyName }}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="close-button"
              aria-label="Закрыть окно"
              :disabled="isSaving"
              @click="closeModal"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <!-- =====================================================
               BLUE TITLE BANNER
          ====================================================== -->

          <div class="intro-banner">
            <div class="banner-decoration banner-decoration-one"></div>
            <div class="banner-decoration banner-decoration-two"></div>

            <div class="intro-banner-content">
              <div class="eyebrow">
                <span class="eyebrow-dot"></span>
                ПОДТВЕРЖДЕНИЕ РОЛИ
              </div>

              <h2 id="role-title" class="modal-title">
                Кем вы работали в компании
                <span class="title-company-name">
                  «{{ companyEmployee.companyName }}»?
                </span>
              </h2>

              <p
                id="role-description"
                class="modal-description"
              >
                Мы нашли связь вашего профиля с этой компанией.
                Расскажите, какую роль вы в ней занимали,
                чтобы мы могли правильно отразить ваш опыт в HR3.
              </p>
            </div>

            <div class="banner-corner-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3L20 6V11C20 16 16.5 19.5 12 21C7.5 19.5 4 16 4 11V6L12 3Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 12L11 14L15 10"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>

          <!-- =====================================================
               ROLES SECTION
          ====================================================== -->

          <div class="roles-section-header">
            <div class="roles-section-title">
              Выберите вашу роль
            </div>

            <div class="roles-section-subtitle">
              Один из доступных вариантов
            </div>
          </div>

          <div
            class="roles-grid"
            aria-label="Варианты ролей"
          >
            <!-- =================================================
                 LEVEL 1 — CEO / LOCKED
            ================================================== -->

            <div
              class="role-card locked"
              aria-disabled="true"
              title="Роль генерального директора подтверждается отдельно"
            >
              <div class="card-top">
                <div class="card-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 9L8 13L12 6L16 13L20 9L18 19H6L4 9Z"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7 22H17"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div class="lock-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="5"
                      y="10"
                      width="14"
                      height="11"
                      rx="2"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                    <path
                      d="M8 10V7A4 4 0 0116 7V10"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                  </svg>
                </div>
              </div>

              <div class="card-content">
                <span class="card-title">
                  CEO / Директор
                </span>

                <span class="card-subtitle">
                  Возглавляли компанию, определяли её стратегию
                  и отвечали за ключевые бизнес-решения.
                  Эта роль требует отдельного подтверждения.
                </span>
              </div>

              <span class="card-status locked-status">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect
                    x="5"
                    y="10"
                    width="14"
                    height="11"
                    rx="2"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />
                  <path
                    d="M8 10V7A4 4 0 0116 7V10"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />
                </svg>

                Недоступно
              </span>
            </div>

            <!-- =================================================
                 LEVEL 2 — MANAGER
            ================================================== -->

            <button
              type="button"
              class="role-card selectable"
              :class="{ selected: selectedLevel === 2 }"
              :disabled="isSaving"
              :aria-pressed="selectedLevel === 2"
              @click="selectLevel(2)"
            >
              <div class="card-top">
                <div class="card-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="7"
                      r="3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                    <path
                      d="M5 20V18C5 14.7 8 13 12 13C16 13 19 14.7 19 18V20"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                    <path
                      d="M19 5V9M17 7H21"
                      stroke="currentColor"
                      stroke-width="1.4"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div class="radio-indicator">
                  <span></span>
                </div>
              </div>

              <div class="card-content">
                <span class="card-title">
                  Руководитель
                </span>

                <span class="card-subtitle">
                  Управляли командой, отделом или отдельным
                  направлением. Организовывали работу,
                  распределяли задачи и отвечали за результаты.
                </span>
              </div>

              <span
                class="card-status"
                :class="{ 'selected-status': selectedLevel === 2 }"
              >
                <svg
                  v-if="selectedLevel === 2"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 12L10 17L19 7"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                {{
                  selectedLevel === 2
                    ? "Выбрано"
                    : "Уровень 2"
                }}
              </span>
            </button>

            <!-- =================================================
                 LEVEL 3 — EMPLOYEE
            ================================================== -->

            <button
              type="button"
              class="role-card selectable"
              :class="{ selected: selectedLevel === 3 }"
              :disabled="isSaving"
              :aria-pressed="selectedLevel === 3"
              @click="selectLevel(3)"
            >
              <div class="card-top">
                <div class="card-icon">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3.5"
                      y="7"
                      width="17"
                      height="13"
                      rx="2.5"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                    <path
                      d="M8.5 7V5.5C8.5 4.4 9.4 3.5 10.5 3.5H13.5C14.6 3.5 15.5 4.4 15.5 5.5V7"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />
                    <path
                      d="M3.5 12.5C8.5 15.2 15.5 15.2 20.5 12.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div class="radio-indicator">
                  <span></span>
                </div>
              </div>

              <div class="card-content">
                <span class="card-title">
                  Сотрудник
                </span>

                <span class="card-subtitle">
                  Работали в компании по своей специальности,
                  выполняли профессиональные задачи
                  и участвовали в проектах без руководства командой.
                </span>
              </div>

              <span
                class="card-status"
                :class="{ 'selected-status': selectedLevel === 3 }"
              >
                <svg
                  v-if="selectedLevel === 3"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 12L10 17L19 7"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                {{
                  selectedLevel === 3
                    ? "Выбрано"
                    : "Уровень 3"
                }}
              </span>
            </button>
          </div>

          <!-- =====================================================
               FOOTER
          ====================================================== -->

          <div class="modal-footer">
            <div class="footer-note">
              <svg viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
                <path
                  d="M12 11V16M12 8H12.01"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>

              <span>
                Вы сможете продолжить позже
              </span>
            </div>

            <div class="footer-actions">
              <button
                type="button"
                class="later-button"
                :disabled="isSaving"
                @click="closeModal"
              >
                Позже
              </button>

              <button
                type="button"
                class="continue-button"
                :disabled="selectedLevel === null || isSaving"
                @click="confirmSelection"
              >
                <span>
                  {{
                    isSaving
                      ? "Сохранение..."
                      : "Продолжить"
                  }}
                </span>

                <span
                  v-if="isSaving"
                  class="loading-spinner"
                ></span>

                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 12H19M19 12L13 6M19 12L13 18"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount
} from "vue";

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },

  companyEmployee: {
    type: Object,
    default: null
  },

  isSaving: {
    type: Boolean,
    default: false
  }
});

// ============================================================
// EMITS
// ============================================================

const emit = defineEmits([
  "select",
  "close"
]);

// ============================================================
// STATE
// ============================================================

const selectedLevel = ref(null);

const modalRef = ref(null);

const isDarkTheme = ref(false);

let themeObserver = null;

// ============================================================
// THEME
// ============================================================

function updateTheme() {
  isDarkTheme.value =
    document.documentElement.getAttribute(
      "data-theme"
    ) === "dark";
}

// ============================================================
// LEVEL SELECTION
// ============================================================

function selectLevel(level) {
  if (props.isSaving) {
    return;
  }

  // CEO (level 1) выбрать нельзя.
  if (level !== 2 && level !== 3) {
    return;
  }

  selectedLevel.value = level;
}

// ============================================================
// CONFIRM
// ============================================================

function confirmSelection() {
  if (props.isSaving) {
    return;
  }

  if (![2, 3].includes(selectedLevel.value)) {
    return;
  }

  if (!props.companyEmployee) {
    return;
  }

  emit("select", {
    relationId: props.companyEmployee.id,
    companyId: props.companyEmployee.companyId,
    companyName: props.companyEmployee.companyName,
    workerId: props.companyEmployee.workerId,
    level: selectedLevel.value
  });
}

// ============================================================
// CLOSE
// ============================================================

function closeModal() {
  if (props.isSaving) {
    return;
  }

  emit("close");
}

// ============================================================
// KEYBOARD
// ============================================================

function handleKeydown(event) {
  if (!props.visible) {
    return;
  }

  if (event.key === "Escape") {
    event.stopPropagation();
    closeModal();
  }
}

// ============================================================
// RESET SELECTION
// ============================================================

watch(
  () => [
    props.visible,
    props.companyEmployee?.id
  ],
  async ([visible]) => {
    selectedLevel.value = null;

    if (visible) {
      await nextTick();
      modalRef.value?.focus();
    }
  }
);

// ============================================================
// LIFECYCLE
// ============================================================

onMounted(() => {
  updateTheme();

  themeObserver = new MutationObserver(
    updateTheme
  );

  themeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ["data-theme"]
    }
  );

  document.addEventListener(
    "keydown",
    handleKeydown,
    true
  );

  if (props.visible) {
    nextTick(() => {
      modalRef.value?.focus();
    });
  }
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();

  themeObserver = null;

  document.removeEventListener(
    "keydown",
    handleKeydown,
    true
  );
});
</script>

<style scoped>

/* ============================================================
   OVERLAY — LIGHT
============================================================ */

.role-overlay {
  --role-bg: #ffffff;
  --role-text: #222b38;
  --role-secondary: #818d9d;
  --role-muted: #a0aaba;

  --role-border: rgba(31, 55, 90, 0.10);

  --role-hover: #f8faff;
  --role-icon-bg: #f4f6fa;
  --role-disabled-bg: #f7f8fa;

  --role-accent: #2173e5;
  --role-accent-soft: rgba(33, 115, 229, 0.08);

  position: fixed;
  inset: 0;

  z-index: 10000;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 88px 24px 24px;

  box-sizing: border-box;

  overflow-y: auto;

  background: rgba(0, 0, 0, 0.72);

  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* ============================================================
   DARK THEME
============================================================ */

.role-overlay.dark-theme {
  --role-bg: #161616;
  --role-text: #f1f3f6;
  --role-secondary: #9da6b2;
  --role-muted: #747d89;

  --role-border: rgba(255, 255, 255, 0.10);

  --role-hover: #222222;
  --role-icon-bg: #282828;
  --role-disabled-bg: #1d1d1d;

  --role-accent: #83b6ff;
  --role-accent-soft: rgba(111, 168, 255, 0.12);

  background: rgba(0, 0, 0, 0.80);
}

/* ============================================================
   MODAL
============================================================ */

.role-modal {
  width: 640px;
  max-width: 100%;

  max-height: calc(100dvh - 112px);

  flex-shrink: 0;

  padding: 26px 30px 24px;

  background: var(--role-bg);
  color: var(--role-text);

  border: 1px solid var(--role-border);
  border-radius: 22px;

  box-shadow:
    0 30px 90px rgba(0, 0, 0, 0.28);

  box-sizing: border-box;

  overflow-y: auto;

  outline: none;
}

.role-overlay.dark-theme .role-modal {
  box-shadow:
    0 30px 100px rgba(0, 0, 0, 0.60);
}

/* ============================================================
   TOP / COMPANY IDENTITY
============================================================ */

.modal-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 14px;
}

.company-identity {
  display: flex;
  align-items: center;

  gap: 12px;

  min-width: 0;
}

.brand-icon {
  width: 44px;
  height: 44px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: var(--role-accent-soft);
  color: var(--role-accent);

  border-radius: 13px;
}

.brand-icon svg {
  width: 23px;
  height: 23px;
}

.company-identity-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 6px;

  min-width: 0;
}

.company-identity-label {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;

  letter-spacing: 0.9px;

  color: var(--role-secondary);
}

.company-name-highlight {
  display: inline-flex;
  align-items: center;

  gap: 8px;

  max-width: 100%;

  padding: 6px 10px;

  border-radius: 8px;

  background: var(--role-accent-soft);

  color: var(--role-accent);

  box-sizing: border-box;
}

.company-name-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;

  background: var(--role-accent);
}

.company-name-text {
  min-width: 0;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: 600;

  line-height: 1.4;

  overflow-wrap: anywhere;
}

.close-button {
  width: 29px;
  height: 29px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 9px;

  background: transparent;
  color: var(--role-muted);

  cursor: pointer;

  transition:
    background 0.18s ease,
    color 0.18s ease;
}

.close-button:hover {
  background: var(--role-icon-bg);
  color: var(--role-text);
}

.close-button svg {
  width: 17px;
  height: 17px;
}

/* ============================================================
   BLUE TITLE BANNER
============================================================ */

.intro-banner {
  position: relative;

  margin-top: 20px;

  padding: 23px 24px 22px;

  overflow: hidden;

  background: #2173e5;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;

  color: #ffffff;

  box-sizing: border-box;
}

.intro-banner-content {
  position: relative;
  z-index: 2;

  max-width: 100%;
}

.banner-decoration {
  position: absolute;

  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 50%;

  pointer-events: none;
}

.banner-decoration-one {
  width: 220px;
  height: 220px;

  right: -85px;
  top: -145px;
}

.banner-decoration-two {
  width: 160px;
  height: 160px;

  right: -95px;
  bottom: -115px;
}

.eyebrow {
  display: flex;
  align-items: center;

  gap: 7px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;

  font-weight: 600;
  letter-spacing: 1px;

  color: rgba(255, 255, 255, 0.79);
}

.eyebrow-dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #ffffff;
}

.modal-title {
  margin: 12px 0 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 24px;
  font-weight: 700;

  line-height: 1.35;
  letter-spacing: -0.5px;

  color: #ffffff;

  overflow-wrap: anywhere;
}

.title-company-name {
  display: block;

  color: #ffffff;
}

.modal-description {
  max-width: 490px;

  margin: 11px 0 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;

  line-height: 1.65;

  color: rgba(255, 255, 255, 0.83);
}

.banner-corner-icon {
  position: absolute;

  right: 20px;
  top: 20px;

  width: 21px;
  height: 21px;

  color: rgba(255, 255, 255, 0.58);
}

.banner-corner-icon svg {
  width: 100%;
  height: 100%;
}

/* ============================================================
   ROLES SECTION HEADER
============================================================ */

.roles-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  margin-top: 24px;
}

.roles-section-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  font-weight: 600;

  color: var(--role-text);
}

.roles-section-subtitle {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;

  color: var(--role-secondary);
}

/* ============================================================
   ROLE GRID
============================================================ */

.roles-grid {
  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 10px;

  margin-top: 12px;
}

/* ============================================================
   ROLE CARD
============================================================ */

.role-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;

  gap: 13px;

  min-width: 0;
  min-height: 205px;

  padding: 15px 13px 13px;

  border: 1px solid var(--role-border);
  border-radius: 14px;

  background: var(--role-bg);

  color: var(--role-text);

  box-sizing: border-box;

  text-align: left;

  font-family: "Eastman-Regular", sans-serif;

  transition:
    border-color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-icon {
  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;

  background: var(--role-icon-bg);
  color: var(--role-secondary);
}

.card-icon svg {
  width: 19px;
  height: 19px;
}

.card-content {
  display: flex;
  flex-direction: column;

  gap: 7px;

  min-width: 0;
}

.card-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: 600;

  line-height: 1.35;

  color: var(--role-text);
}

.card-subtitle {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;

  line-height: 1.55;

  color: var(--role-secondary);
}

.card-status {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  align-self: flex-start;

  margin-top: auto;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;

  color: var(--role-muted);
}

.card-status svg {
  width: 12px;
  height: 12px;

  flex-shrink: 0;
}

/* ============================================================
   LOCKED CEO
============================================================ */

.role-card.locked {
  background: var(--role-disabled-bg);

  border-style: dashed;

  cursor: not-allowed;
}

.role-card.locked .card-icon {
  opacity: 0.55;
}

.role-card.locked .card-title,
.role-card.locked .card-subtitle,
.role-card.locked .card-status {
  color: var(--role-muted);
}

.lock-icon {
  width: 17px;
  height: 17px;

  color: var(--role-muted);
}

.lock-icon svg {
  width: 100%;
  height: 100%;
}

/* ============================================================
   SELECTABLE CARDS
============================================================ */

.role-card.selectable {
  cursor: pointer;
}

.role-card.selectable:hover:not(:disabled) {
  background: var(--role-hover);

  border-color: rgba(33, 115, 229, 0.35);

  transform: translateY(-2px);
}

.role-card.selectable.selected {
  background: var(--role-accent-soft);

  border-color: var(--role-accent);

  box-shadow:
    0 0 0 1px var(--role-accent);
}

.role-card.selected .card-icon {
  background: var(--role-bg);
  color: var(--role-accent);
}

.role-card.selected .card-title {
  color: var(--role-accent);
}

.role-card.selected .card-status {
  color: var(--role-accent);
}

.role-card.selectable:disabled {
  cursor: not-allowed;
}

/* ============================================================
   RADIO INDICATOR
============================================================ */

.radio-indicator {
  width: 18px;
  height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border: 1.5px solid var(--role-border);
  border-radius: 50%;

  box-sizing: border-box;
}

.role-card.selected .radio-indicator {
  border-color: var(--role-accent);
  background: var(--role-accent);
}

.radio-indicator span {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: #ffffff;

  opacity: 0;
}

.role-card.selected .radio-indicator span {
  opacity: 1;
}

/* ============================================================
   FOOTER
============================================================ */

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  margin-top: 23px;
  padding-top: 19px;

  border-top: 1px solid var(--role-border);
}

.footer-note {
  display: flex;
  align-items: center;

  gap: 7px;

  min-width: 0;

  color: var(--role-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;

  line-height: 1.45;
}

.footer-note svg {
  width: 15px;
  height: 15px;

  flex-shrink: 0;
}

.footer-actions {
  display: flex;
  align-items: center;

  gap: 9px;

  flex-shrink: 0;
}

/* ============================================================
   BUTTONS
============================================================ */

.later-button,
.continue-button {
  min-height: 39px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding: 10px 15px;

  border: none;
  border-radius: 10px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;

  cursor: pointer;

  transition:
    background 0.18s ease,
    opacity 0.18s ease,
    transform 0.18s ease;
}

.later-button {
  background: var(--role-icon-bg);
  color: var(--role-secondary);
}

.later-button:hover:not(:disabled) {
  background: var(--role-hover);
  color: var(--role-text);
}

.continue-button {
  min-width: 125px;

  background: #2173e5;
  color: #ffffff;
}

.continue-button:hover:not(:disabled) {
  background: #1666d3;
}

.continue-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.later-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.continue-button svg {
  width: 16px;
  height: 16px;
}

.continue-button:active:not(:disabled),
.later-button:active:not(:disabled) {
  transform: scale(0.98);
}

/* ============================================================
   FOCUS
============================================================ */

.close-button:focus-visible,
.role-card:focus-visible,
.later-button:focus-visible,
.continue-button:focus-visible {
  outline: 2px solid var(--role-accent);
  outline-offset: 3px;
}

/* ============================================================
   SPINNER
============================================================ */

.loading-spinner {
  width: 14px;
  height: 14px;

  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;

  border-radius: 50%;

  animation: roleSpin 0.7s linear infinite;
}

@keyframes roleSpin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================
   TRANSITIONS
============================================================ */

.role-modal-enter-active,
.role-modal-leave-active {
  transition: opacity 0.2s ease;
}

.role-modal-enter-active .role-modal,
.role-modal-leave-active .role-modal {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.role-modal-enter-from,
.role-modal-leave-to {
  opacity: 0;
}

.role-modal-enter-from .role-modal {
  transform: translateY(13px) scale(0.98);
}

.role-modal-leave-to .role-modal {
  transform: translateY(8px) scale(0.985);
}

/* ============================================================
   TABLET
============================================================ */

@media (max-width: 650px) {
  .role-overlay {
    padding: 40px 14px 14px;
  }

  .role-modal {
    padding: 23px 20px 20px;

    max-height: calc(100dvh - 54px);
  }

  .modal-title {
    font-size: 22px;
  }

  .roles-grid {
    gap: 7px;
  }

  .role-card {
    padding: 12px 9px;
  }

  .intro-banner {
    padding: 20px 19px;
  }
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 480px) {
  .role-overlay {
    padding: 18px 12px 12px;
  }

  .role-modal {
    padding: 20px 17px;

    border-radius: 18px;

    max-height: calc(100dvh - 30px);
  }

  .company-identity {
    gap: 9px;
  }

  .brand-icon {
    width: 39px;
    height: 39px;
  }

  .brand-icon svg {
    width: 21px;
    height: 21px;
  }

  .company-name-text {
    font-size: 11px;
  }

  .intro-banner {
    margin-top: 18px;

    padding: 19px 17px;

    border-radius: 14px;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-description {
    font-size: 11px;
  }

  .banner-corner-icon {
    right: 14px;
    top: 14px;
  }

  .roles-section-header {
    align-items: flex-start;
    flex-direction: column;

    gap: 4px;

    margin-top: 20px;
  }

  .roles-grid {
    grid-template-columns: 1fr;

    margin-top: 11px;
  }

  .role-card {
    position: relative;

    min-height: 100px;

    flex-direction: row;
    align-items: center;

    gap: 11px;

    padding: 13px 42px 13px 12px;
  }

  .card-top {
    flex-shrink: 0;
  }

  .card-content {
    flex: 1;
  }

  .card-status {
    display: none;
  }

  .radio-indicator,
  .lock-icon {
    position: absolute;
    right: 12px;
  }

  .card-title {
    font-size: 11px;
  }

  .card-subtitle {
    font-size: 10px;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .footer-actions {
    width: 100%;
  }

  .later-button,
  .continue-button {
    flex: 1;
  }
}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {
  .role-modal-enter-active,
  .role-modal-leave-active,
  .role-modal-enter-active .role-modal,
  .role-modal-leave-active .role-modal,
  .role-card,
  .later-button,
  .continue-button {
    transition: none;
  }

  .loading-spinner {
    animation: none;
  }
}

</style>
